import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  ErrorCode,
  type ContactProfileValues,
  type DocumentRequestChannel,
  type UpdateContactBody,
} from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Tabs, useToast } from '../../ds/components';
import { useIsCompact, useIsMobile } from '../../ds/hooks';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import {
  calculateFinancing,
  cancelMessage,
  completeTask,
  keepDuplicateSeparate,
  lockedDetailsOf,
  recordContactOpened,
  sendMessageNow,
  updateContact,
  updateContactProfile,
  useContactDocuments,
  useContactRecord,
  useInvalidateContact,
  useSelfDisclosures,
  useTaskDetail,
  useTimeline,
} from './api';
import { AssignDialog, type AssignKind } from './AssignDialog';
import { DncDialog } from './DncDialog';
import { LifecycleDialog } from './LifecycleDialog';
import { AppointmentEditor } from './editors/AppointmentEditor';
import { CallEditor } from './editors/CallEditor';
import { DocumentRequestEditor } from './editors/DocumentRequestEditor';
import { EmailEditor } from './editors/EmailEditor';
import { NoteEditor } from './editors/NoteEditor';
import { SelfDisclosureEditor } from './editors/SelfDisclosureEditor';
import { TaskEditor } from './editors/TaskEditor';
import { WhatsAppEditor } from './editors/WhatsAppEditor';
import { useNextStepGate, type EditorResult } from './editors/shared';
import { confirmAppointmentRequest, usePortalCard } from './portal-api';
import { Badge, GhostButton, PaperButton, RecordAlert } from './record/bits';
import { ChangeRequestsCard } from './record/ChangeRequestsCard';
import { DocumentBrowser } from './record/DocumentBrowser';
import { DocumentsCard } from './record/DocumentsCard';
import { FinancingBand } from './record/FinancingBand';
import { GdprDeletedBanner } from './record/GdprDeletedBanner';
import { LinkedObjects } from './record/LinkedObjects';
import { LockedRecord } from './record/LockedRecord';
import { PortalCard } from './record/PortalCard';
import { PortalInviteAction } from './record/PortalInviteAction';
import { PortalRuleCard } from './record/PortalRuleCard';
import { ProfileDialog } from './record/ProfileDialog';
import { PropertiesCard, type PropertyAction } from './record/PropertiesCard';
import { RecordSkeleton } from './record/RecordSkeleton';
import { Timeline } from './record/Timeline';
import { Toolbar } from './record/Toolbar';
import { UpcomingBlock } from './record/UpcomingBlock';
import { UploadDialog } from './record/UploadDialog';
import type { ContactRecord, DocumentRow, TaskDetail, TimelineFilter, ToolKey, UpcomingItem } from './types';
import { knownFromRef } from './users';
import styles from './record/record.module.css';

type EditorKey =
  'call' | 'email' | 'whatsapp' | 'appointment' | 'task' | 'documents' | 'selfDisclosure' | 'note';

const EDITOR_KEYS: ReadonlySet<string> = new Set([
  'call',
  'email',
  'whatsapp',
  'appointment',
  'task',
  'documents',
  'selfDisclosure',
  'note',
]);

const TOOL_EDITOR: Record<ToolKey, EditorKey> = {
  call: 'call',
  email: 'email',
  whatsapp: 'whatsapp',
  appointment: 'appointment',
  task: 'task',
  document: 'documents',
  selfDisclosure: 'selfDisclosure',
  note: 'note',
};

const EMPTY_FILTER: TimelineFilter = { channel: null, kind: null, origin: null };

type MobileTab = 'overview' | 'context' | 'properties';
type MiddleTab = 'timeline' | 'context';

/** The editor a deep-linked task (`?task=`) opens: calls, messages, documents, or the interim tasks of the next-step dialog. */
function editorForTask(task: TaskDetail): EditorKey | null {
  switch (task.type) {
    case 'call':
    case 'follow_up':
    case 'chase':
      return 'call';
    case 'email':
      return 'email';
    case 'whatsapp':
      return 'whatsapp';
    case 'document':
      return 'documents';
    default:
      break;
  }
  if (/^(Termin buchen|Folgetermin buchen)/.test(task.title)) return 'appointment';
  if (/^Selbstauskunft-Link senden/.test(task.title)) return 'selfDisclosure';
  return null;
}

/** D-439: the toolbar explains only a real lock (a duplicate waiting for its decision), nothing about the screen. */
function toolbarNote(record: ContactRecord): string | null {
  if (record.banners.duplicate && !record.banners.dnc)
    return 'Kanäle bleiben gesperrt, bis die Dublette entschieden ist.';
  return null;
}

function documentsEmptyText(record: ContactRecord): string | null {
  if (record.banners.duplicate) {
    return 'Keine — der Treffer im Bestand behält seine Dokumente. Beim Zusammenführen werden sie übernommen.';
  }
  return null;
}

/** Why „Hochladen" of the documents card is locked (D-445): only an undecided duplicate or a GDPR deletion. */
function documentsUploadLockedReason(record: ContactRecord): string | null {
  if (record.gdprDeleted) return 'Der Kontakt wurde nach DSGVO gelöscht.';
  if (record.banners.duplicate) {
    return 'Hochladen ist gesperrt, bis die Dublette entschieden ist — der Treffer im Bestand behält seine Dokumente.';
  }
  return null;
}

function crumbLabel(record: ContactRecord): string {
  const label = record.header.lifecycle.label;
  if (record.banners.dnc) return `${label} · gesperrt`;
  if (record.banners.duplicate) return `${label} · Prüfung`;
  return label;
}

/** Kontaktakte I.01–I.12 with the editors II.E1–E8: the record at `/contacts/:id`. */
export function ContactRecordPage() {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const mobile = useIsMobile();
  const compact = useIsCompact();
  const invalidate = useInvalidateContact();
  const workspace = useWorkspace();
  const portalOn = workspace.modules.includes('client_portal');

  const record = useContactRecord(id);
  const data = record.data;
  const editorParam = params.get('editor');
  const taskParam = params.get('task');
  const taskQuery = useTaskDetail(taskParam);
  const task = taskQuery.data && taskQuery.data.contactId === id ? taskQuery.data : null;
  const editor: EditorKey | null =
    editorParam && EDITOR_KEYS.has(editorParam)
      ? (editorParam as EditorKey)
      : task && !editorParam
        ? editorForTask(task)
        : null;
  const taskWithoutEditor = task !== null && editor === null;

  const [filter, setFilter] = useState<TimelineFilter>(EMPTY_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'lifecycle' | 'dnc' | 'owner' | 'referrer' | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [mobileTab, setMobileTab] = useState<MobileTab>('overview');
  const [middleTab, setMiddleTab] = useState<MiddleTab>('timeline');
  const [completing, setCompleting] = useState<string | null>(null);
  /** The request editor opened from the portal documents (Mandantenportal 29) preselects „nur Portal". */
  const [requestChannel, setRequestChannel] = useState<DocumentRequestChannel>('email');
  const timelineRef = useRef<HTMLDivElement>(null);

  const timeline = useTimeline(id, filter, record.isSuccess);
  const documents = useContactDocuments(id, record.isSuccess);
  const portalCard = usePortalCard(id, record.isSuccess && portalOn);

  // A „Selbstauskunft prüfen" step (`?task=`) opens the internal self-disclosure page (Selbstauskunft 08) of the
  // newest signed version — the M4 module, not an editor of the record.
  const reviewTask =
    task !== null && (task.type === 'review_self_disclosure' || task.type === 'self_disclosure_changed');
  const selfDisclosures = useSelfDisclosures(id, reviewTask);
  useEffect(() => {
    if (!reviewTask || !selfDisclosures.data) return;
    const version =
      selfDisclosures.data.items.find((item) => item.status === 'submitted' || item.status === 'reviewed') ??
      selfDisclosures.data.items[0];
    if (version) void navigate(`/contacts/${id}/self-disclosure/${version.id}`, { replace: true });
  }, [id, navigate, reviewTask, selfDisclosures.data]);

  useEffect(() => {
    if (record.isSuccess) recordContactOpened(id);
  }, [id, record.isSuccess]);

  useEffect(() => {
    setFilter(EMPTY_FILTER);
    setFilterOpen(false);
    setEditMode(false);
    setProfileOpen(false);
    setBrowserOpen(false);
    setMobileTab('overview');
  }, [id]);

  const setEditor = useCallback(
    (next: EditorKey | null, taskId: string | null = null) => {
      setParams(
        (current) => {
          const search = new URLSearchParams(current);
          if (next) search.set('editor', next);
          else search.delete('editor');
          if (taskId) search.set('task', taskId);
          else search.delete('task');
          return search;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  // `invalidate` covers the timeline as well (it lives under the record's query root).
  const refresh = useCallback(() => invalidate(id), [id, invalidate]);

  async function onEditorDone(result: EditorResult) {
    setEditor(null);
    toast({ message: result.toast });
    await refresh();
    if (result.next?.editor === 'appointment') setEditor('appointment', result.next.completesTaskId);
  }

  const completeGate = useNextStepGate(data?.header.name ?? '', async (step) => {
    if (!completing) return;
    const done = await completeTask(completing, { nextStep: step });
    setCompleting(null);
    toast({ message: 'Aufgabe erledigt.' });
    setEditor(null);
    await refresh();
    if (done.next === 'book_appointment') setEditor('appointment', done.createdTaskId);
  });

  async function completeUpcoming(taskId: string) {
    setBusyId(`${taskId}:complete`);
    setCompleting(taskId);
    try {
      const done = await completeTask(taskId, {});
      setCompleting(null);
      toast({ message: 'Aufgabe erledigt.' });
      setEditor(null);
      await refresh();
      if (done.next === 'book_appointment') setEditor('appointment', done.createdTaskId);
    } catch (error) {
      if (error instanceof ApiError && error.code === ErrorCode.NEXT_STEP_REQUIRED) completeGate.open();
      else {
        setCompleting(null);
        toast({ message: errorMessage(error) });
      }
    } finally {
      setBusyId(null);
    }
  }

  async function onUpcomingAction(item: UpcomingItem, action: string) {
    switch (action) {
      case 'call':
        setEditor('call', item.kind === 'task' ? item.id : null);
        return;
      case 'edit':
      case 'advance':
        setEditor('task', item.id);
        return;
      case 'reschedule':
        await navigate(`/calendar/appointments/${item.id}`);
        return;
      case 'confirm_request':
        setBusyId(`${item.id}:${action}`);
        try {
          await confirmAppointmentRequest(item.id);
          toast({ message: 'Termin bestätigt.' });
          await refresh();
        } catch (error) {
          toast({ message: errorMessage(error) });
        } finally {
          setBusyId(null);
        }
        return;
      case 'complete':
        await completeUpcoming(item.id);
        return;
      case 'send_now':
      case 'cancel': {
        setBusyId(`${item.id}:${action}`);
        try {
          if (action === 'send_now') {
            await sendMessageNow(item.id);
            toast({ message: 'Nachricht wird jetzt gesendet.' });
          } else {
            await cancelMessage(item.id);
            toast({ message: 'Geplanter Versand abgebrochen.' });
          }
          await refresh();
        } catch (error) {
          toast({ message: errorMessage(error) });
        } finally {
          setBusyId(null);
        }
        return;
      }
      default:
        return;
    }
  }

  async function saveProperties(body: UpdateContactBody) {
    setPropertiesError(null);
    try {
      const result = await updateContact(id, body);
      if (result.handedOver || result.record === null) {
        toast({ message: 'Der Kontakt wurde übergeben und liegt nicht mehr in Ihrem Bereich.' });
        await navigate('/contacts');
        return;
      }
      await refresh();
    } catch (error) {
      const message =
        error instanceof ApiError && error.code === ErrorCode.VALIDATION_FAILED
          ? (Object.values(error.fields)[0] ?? error.message)
          : errorMessage(error);
      setPropertiesError(message);
      throw error;
    }
  }

  /** „Lead-Angaben" in the panel (D-341): the same PATCH as „Alle Eigenschaften". */
  async function saveProfileProperties(body: ContactProfileValues) {
    setPropertiesError(null);
    try {
      await updateContactProfile(id, body);
      await refresh();
    } catch (error) {
      const message =
        error instanceof ApiError && error.code === ErrorCode.VALIDATION_FAILED
          ? (Object.values(error.fields)[0] ?? error.message)
          : errorMessage(error);
      setPropertiesError(message);
      throw error;
    }
  }

  /** „Jetzt berechnen“ / „Neu berechnen“ of the financing band (D-380). */
  async function recalculateFinancing() {
    try {
      await calculateFinancing(id);
      toast({ message: 'Finanzierbarkeit berechnet.' });
      await refresh();
    } catch (error) {
      toast({ message: errorMessage(error) });
    }
  }

  async function keepSeparate(candidateId: string) {
    setBusyId(`${candidateId}:keep`);
    try {
      await keepDuplicateSeparate(candidateId);
      toast({ message: 'Dublette entschieden: getrennt lassen.' });
      await refresh();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusyId(null);
    }
  }

  function showDncEntry() {
    setFilter({ channel: null, kind: 'system', origin: null });
    setMobileTab('overview');
    setMiddleTab('timeline');
    timelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function openRow(row: DocumentRow) {
    if (row.canOpen && row.fileId) window.open(`/api/files/${row.fileId}`, '_blank', 'noopener');
    else setBrowserOpen(true);
  }

  const timelineItems = useMemo(
    () => timeline.data?.pages.flatMap((page) => page.items) ?? [],
    [timeline.data],
  );
  const counts = timeline.data?.pages[0]?.counts ?? data?.counts;

  // --- error states -------------------------------------------------------------------------------
  if (record.isPending) {
    return (
      <div className={styles.page}>
        <nav className={styles.crumbs} aria-label="Brotkrumen">
          <button type="button" className={styles.crumbLink} onClick={() => void navigate('/contacts')}>
            Kontakte
          </button>
          <span className={styles.crumbSeparator}>·</span>
          <span className={styles.crumbCurrent}>wird geladen</span>
        </nav>
        <RecordSkeleton />
      </div>
    );
  }
  if (record.isError) {
    const locked = lockedDetailsOf(record.error);
    const notFound = record.error instanceof ApiError && record.error.status === 404;
    return (
      <div className={styles.page}>
        <nav className={styles.crumbs} aria-label="Brotkrumen">
          <button type="button" className={styles.crumbLink} onClick={() => void navigate('/contacts')}>
            Kontakte
          </button>
          <span className={styles.crumbSeparator}>·</span>
          <span className={styles.crumbCurrent}>
            {locked ? locked.name : notFound ? 'nicht gefunden' : 'Fehler'}
          </span>
        </nav>
        {locked ? (
          <LockedRecord id={id} details={locked} message={record.error.message} />
        ) : (
          <Banner
            tone={notFound ? 'neutral' : 'danger'}
            title={notFound ? 'Der Kontakt wurde nicht gefunden.' : 'Die Akte konnte nicht geladen werden.'}
            actions={
              <>
                <Button variant="secondary" size="small" onClick={() => void navigate('/contacts')}>
                  Zur Kontaktliste
                </Button>
                {notFound ? null : (
                  <Button variant="ghost" size="small" onClick={() => void record.refetch()}>
                    Erneut versuchen
                  </Button>
                )}
              </>
            }
          >
            {notFound
              ? 'Der Link zeigt auf keinen Kontakt in Ihrem Bereich — vielleicht wurde er zusammengeführt.'
              : errorMessage(record.error)}
          </Banner>
        )}
      </div>
    );
  }
  if (!data) return null;

  // --- the record ---------------------------------------------------------------------------------
  const header = data.header;
  const contactLine = [header.phone, header.email, header.city].filter(Boolean).join(' · ');
  const editorTask = task;
  const editorNode: ReactNode = (() => {
    // A GDPR-deleted record keeps no editors (every tool is locked by the server).
    if (!editor || data.gdprDeleted) return null;
    const common = { record: data, task: editorTask, onClose: () => setEditor(null), onDone: onEditorDone };
    switch (editor) {
      case 'call':
        return <CallEditor {...common} />;
      case 'email':
        return <EmailEditor {...common} />;
      case 'whatsapp':
        return <WhatsAppEditor {...common} />;
      case 'appointment':
        return <AppointmentEditor {...common} />;
      case 'task':
        return <TaskEditor {...common} mode={editorTask ? 'reschedule' : 'create'} />;
      case 'documents':
        return <DocumentRequestEditor key={requestChannel} {...common} initialChannel={requestChannel} />;
      case 'selfDisclosure':
        return <SelfDisclosureEditor {...common} />;
      case 'note':
        return <NoteEditor {...common} />;
    }
  })();

  const upcoming = (
    <UpcomingBlock
      items={data.upcoming}
      note={data.upcomingNote}
      highlightId={task?.id ?? null}
      completeId={taskWithoutEditor && task ? task.id : null}
      busyId={busyId}
      onAction={(item, action) => void onUpcomingAction(item, action)}
    />
  );

  const eventCount = counts?.channel.find((entry) => entry.key === 'all')?.count ?? 0;
  const timelineNode = (
    <Timeline
      ref={timelineRef}
      contactId={id}
      counts={counts ?? data.counts}
      filter={filter}
      onFilterChange={setFilter}
      filterOpen={filterOpen}
      onToggleFilter={() => setFilterOpen((open) => !open)}
      items={timelineItems}
      loading={timeline.isPending}
      error={timeline.isError ? timeline.error : null}
      hasMore={timeline.hasNextPage}
      loadingMore={timeline.isFetchingNextPage}
      onLoadMore={() => void timeline.fetchNextPage()}
    >
      {upcoming}
    </Timeline>
  );

  const portal = portalOn ? portalCard.data : undefined;
  // Customers carry the invitation in the head (D-334): sent deliberately, never with the status change.
  const headPortal =
    portal &&
    (header.lifecycle.key === 'customer' || header.lifecycle.key === 'existing_customer') &&
    (portal.status.state === 'active' || portal.actions.invite.allowed)
      ? portal
      : null;
  const portalNode = portalOn ? (
    <>
      <PortalCard
        contactId={id}
        contactName={header.name}
        email={header.email}
        card={portalCard.data}
        loading={portalCard.isPending}
        error={portalCard.isError ? portalCard.error : null}
        onRetry={() => void portalCard.refetch()}
      />
      {portal && portal.status.state !== 'none' ? <PortalRuleCard rule={portal.rule} /> : null}
      {portal ? <ChangeRequestsCard contactId={id} requests={portal.changeRequests} /> : null}
    </>
  ) : null;
  const linkedNode = <LinkedObjects groups={data.linked} />;
  const leftColumn = (
    <div className={styles.column}>
      <DocumentsCard
        rows={documents.data?.items ?? null}
        loading={documents.isPending}
        emptyText={documentsEmptyText(data)}
        onOpenAll={() => setBrowserOpen(true)}
        onOpenRow={openRow}
        onUpload={() => setUploadOpen(true)}
        uploadLockedReason={documentsUploadLockedReason(data)}
      />
      <PropertiesCard
        groups={data.properties}
        editNote={data.editNote}
        editMode={editMode}
        onToggleEdit={() => {
          setEditMode((mode) => !mode);
          setPropertiesError(null);
        }}
        onOpenAll={() => setProfileOpen(true)}
        onSave={saveProperties}
        onSaveProfile={saveProfileProperties}
        onAction={(action: PropertyAction) => setDialog(action)}
        error={propertiesError}
      />
    </div>
  );

  // The disqualified banner moves the „Wiedervorlage" task (the follow-up), not any other open task.
  const followUpTask =
    data.upcoming.find((item) => item.kind === 'task' && /^Wiedervorlage/.test(item.title)) ??
    [...data.upcoming].reverse().find((item) => item.kind === 'task') ??
    null;

  const banners = (
    <>
      {data.gdprDeleted ? <GdprDeletedBanner deleted={data.gdprDeleted} /> : null}
      {data.banners.dnc ? (
        <RecordAlert
          tone="danger"
          title={data.banners.dnc.title}
          text={data.banners.dnc.text}
          actions={<PaperButton onClick={showDncEntry}>DNC-Eintrag ansehen</PaperButton>}
        />
      ) : null}
      {data.banners.duplicate ? (
        <RecordAlert
          tone="warning"
          title={data.banners.duplicate.title}
          text={data.banners.duplicate.text}
          actions={
            data.banners.duplicate.canDecide ? (
              <>
                <PaperButton
                  onClick={() =>
                    void navigate(`/tasks/duplicates/${data.banners.duplicate?.candidateId ?? ''}`)
                  }
                >
                  Zusammenführen
                </PaperButton>
                <GhostButton
                  disabled={busyId === `${data.banners.duplicate.candidateId}:keep`}
                  onClick={() => void keepSeparate(data.banners.duplicate?.candidateId ?? '')}
                >
                  Getrennt lassen
                </GhostButton>
              </>
            ) : null
          }
        />
      ) : null}
      {data.banners.disqualified ? (
        <RecordAlert
          tone="info"
          title={data.banners.disqualified.title}
          text={data.banners.disqualified.text}
          actions={
            <>
              <PaperButton onClick={() => setEditor('call')}>Jetzt reaktivieren</PaperButton>
              {followUpTask ? (
                <GhostButton onClick={() => setEditor('task', followUpTask.id)}>
                  Wiedervorlage verschieben
                </GhostButton>
              ) : null}
            </>
          }
        />
      ) : null}
    </>
  );

  const toolbar = (
    <Toolbar
      tools={data.tools}
      note={toolbarNote(data)}
      onOpen={(tool) => {
        setRequestChannel('email');
        setEditor(TOOL_EDITOR[tool]);
      }}
    />
  );

  return (
    <div className={styles.page}>
      <nav className={styles.crumbs} aria-label="Brotkrumen">
        <button type="button" className={styles.crumbLink} onClick={() => void navigate('/contacts')}>
          Kontakte
        </button>
        <span className={styles.crumbSeparator}>·</span>
        <span className={styles.crumbCurrent}>{crumbLabel(data)}</span>
      </nav>

      <div className={styles.stack}>
        {banners}
        <div className={styles.head}>
          <div className={styles.headAvatar} aria-hidden="true">
            {header.initials}
          </div>
          <div className={styles.headText}>
            <div className={styles.headTitleRow}>
              <h1 className={styles.headTitle}>{header.name}</h1>
              {header.badges.map((badge) => (
                <Badge key={badge.label} tone={badge.tone}>
                  {badge.label}
                </Badge>
              ))}
            </div>
            <div className={styles.headLine}>{contactLine || 'Keine Kontaktdaten hinterlegt'}</div>
          </div>
          {header.headNote ? <div className={styles.headNote}>{header.headNote}</div> : null}
          {headPortal ? (
            <div className={styles.headPortal}>
              <PortalInviteAction
                contactId={id}
                contactName={header.name}
                email={header.email}
                card={headPortal}
                size="default"
              />
            </div>
          ) : null}
        </div>

        <FinancingBand contactId={id} financing={data.financing} onCalculate={recalculateFinancing} />

        {mobile ? (
          <Tabs<MobileTab>
            label="Akte"
            variant="bare"
            value={mobileTab}
            onChange={setMobileTab}
            items={[
              { value: 'overview', label: 'Übersicht' },
              { value: 'context', label: 'Kontext' },
              { value: 'properties', label: 'Eigenschaften' },
            ]}
          />
        ) : null}

        <div className={styles.grid}>
          {!mobile || mobileTab === 'properties' ? leftColumn : null}

          {!mobile || mobileTab === 'overview' ? (
            <div className={styles.column}>
              {toolbar}
              {editorNode}
              {compact && !mobile ? (
                <Tabs<MiddleTab>
                  label="Verlauf und Kontext"
                  variant="bare"
                  className={styles.middleTabs}
                  value={middleTab}
                  onChange={setMiddleTab}
                  items={[
                    { value: 'timeline', label: 'Verlauf', count: eventCount },
                    { value: 'context', label: 'Kontext' },
                  ]}
                />
              ) : null}
              {compact && !mobile && middleTab === 'context' ? (
                <>
                  {portalNode}
                  {linkedNode}
                </>
              ) : (
                timelineNode
              )}
            </div>
          ) : mobile && editorNode ? (
            editorNode
          ) : null}

          {!mobile || mobileTab === 'context' ? (
            <div className={clsx(styles.column, styles.columnRight)}>
              {portalNode}
              {linkedNode}
            </div>
          ) : null}
        </div>
      </div>

      <DocumentBrowser
        open={browserOpen}
        contactName={header.name}
        documents={documents.data}
        loading={documents.isPending}
        error={documents.isError ? documents.error : null}
        onClose={() => setBrowserOpen(false)}
        onUpload={() => setUploadOpen(true)}
        onRequest={() => {
          setBrowserOpen(false);
          setRequestChannel('email');
          setEditor('documents');
        }}
        onOpenRow={openRow}
        portal={portal ? { contactId: id, documents: portal.documents } : null}
        onRequestPortal={() => {
          setBrowserOpen(false);
          setRequestChannel('portal');
          setEditor('documents');
        }}
      />
      {profileOpen ? (
        <ProfileDialog contactId={id} contactName={header.name} onClose={() => setProfileOpen(false)} />
      ) : null}
      <UploadDialog
        open={uploadOpen}
        contactId={id}
        onClose={() => setUploadOpen(false)}
        onUploaded={(row) => {
          setUploadOpen(false);
          toast({
            message: `Hochgeladen: ${row.name}. Die Datei wird auf Viren geprüft und ist danach abrufbar.`,
          });
          void refresh();
        }}
      />

      <LifecycleDialog
        open={dialog === 'lifecycle'}
        subjects={[
          {
            id,
            name: header.name,
            lifecycle: header.lifecycle,
            owner: knownFromRef(header.owner)[0] ?? null,
          },
        ]}
        onClose={() => setDialog(null)}
        onDone={(result) => {
          setDialog(null);
          toast({ message: `Lifecycle geändert: ${result.single?.lifecycle.label ?? ''}`.trim() });
          void refresh();
        }}
      />
      <DncDialog
        open={dialog === 'dnc'}
        contact={{ id, name: header.name }}
        onClose={() => setDialog(null)}
        onDone={() => {
          setDialog(null);
          toast({ message: 'DNC gesetzt. Anruf, E-Mail und WhatsApp sind deaktiviert.' });
          setEditMode(false);
          void refresh();
        }}
      />
      {dialog === 'owner' || dialog === 'referrer' ? (
        <AssignDialog
          open
          kind={dialog satisfies AssignKind}
          subjects={[{ id, name: header.name, owner: knownFromRef(header.owner)[0] ?? null }]}
          currentId={dialog === 'owner' ? (header.owner?.id ?? null) : null}
          onClose={() => setDialog(null)}
          onDone={(result) => {
            setDialog(null);
            if (result.handedOver) {
              toast({ message: 'Der Kontakt wurde übergeben und liegt nicht mehr in Ihrem Bereich.' });
              void navigate('/contacts');
              return;
            }
            toast({ message: dialog === 'owner' ? 'Besitzer geändert.' : 'Tippgeber geändert.' });
            void refresh();
          }}
        />
      ) : null}
      {completeGate.dialog}
    </div>
  );
}
